import { Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import InvitationsManager from "../../components/users/InvitationManager";
import TeamManagementPanel from "../../components/multi_tenant/TeamManagementPanel";
import Security from "../../components/settings/pages/Security";
import HistoryOfScopes from "../../components/settings/pages/HistoryOfScopes";
import ActivityLogsTable from "../../components/multi_tenant/ActivityLogsTable";
import ActivityLogsFilters from "../../components/multi_tenant/ActivityLogsFilters";
import Integration from "../../components/profile/Integration";
import CompanyIntegrations from "../../components/settings/pages/CompanyIntegrations";
import SSOSettings from "../../components/settings/pages/SSOSettings";
import { useEffect, useMemo, useState } from "react";
import { ActivityLog } from "../../utils/hooks/multi-tenant/invitation";
import Pagination from "../../components/retests/utils/Pagination";
import { getActivityLogs } from "../../utils/api/endpoints/user/multiTenant";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import NotificationSettings from "../../components/settings/NotificationSettings";
import NotificationInfoCard from "../../components/settings/NotificationInfoCard";
import { getOrgResponses } from "../../utils/api/endpoints/questionnaire";

const ActivityLogPage = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [module, setModule] = useState("all");
  const [order, setOrder] = useState<"newest" | "oldest">("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const r: any = await getActivityLogs();
        setLogs(r?.logs || r || []);
        setError(false);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();

    // Listen for refresh events from other modules (e.g., Team Management actions)
    const onRefresh = () => fetchLogs();
    window.addEventListener("ctb-activity-logs-refresh", onRefresh as any);
    return () => window.removeEventListener("ctb-activity-logs-refresh", onRefresh as any);
  }, []);

  const filtered = useMemo(() => {
    // Default: newest first by created_at if available, else keep as is
    let list = [...logs];
    list.sort((a: any, b: any) => new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime());
    if (query) list = list.filter(l => JSON.stringify(l).toLowerCase().includes(query.toLowerCase()));
    if (role !== "all") list = list.filter(l => (l as any)?.role === role);
    if (module !== "all") list = list.filter(l => (l as any)?.module === module);
    if (order === "oldest") list.reverse();
    return list;
  }, [logs, query, role, module, order]);

  // reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [query, role, module, order]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginatedLogs = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage]
  );

  const uniqueRoles = useMemo(() => Array.from(new Set(logs.map(l => (l as any)?.role).filter(Boolean))) as string[], [logs]);
  const uniqueModules = useMemo(() => Array.from(new Set(logs.map(l => (l as any)?.module).filter(Boolean))) as string[], [logs]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>Activity Log</h2>
      </div>
      <ActivityLogsFilters
        searchQuery={query}
        setSearchQuery={setQuery}
        filterRole={role}
        setFilterRole={setRole}
        filterModule={module}
        setFilterModule={setModule}
        sortOrder={order}
        setSortOrder={setOrder}
        availableRoles={[]}
        availableModules={uniqueModules}
      />
      <ActivityLogsTable logs={paginatedLogs} isLoading={loading} isError={error} />
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  );
};

const LeftLink = ({ to, label, icon }: { to: string; label: string; icon: React.ReactNode }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 rounded-lg transition ${isActive ? "bg-[#58CC02]/[0.17] text-[#58CC02]" : "hover:bg-gray-50 text-gray-700"
      }`
    }
    end
  >
    <span className="w-5 h-5">{icon}</span>
    <span className="text-sm font-medium">{label}</span>
  </NavLink>
);

const OrgSettingsPage = () => {
  const navigate = useNavigate();
  const { role } = useUserCredentials();
  const location = useLocation();
  const [hasScopes, setHasScopes] = useState<boolean>(false);

  useEffect(() => {
    if (role && role !== UserRole.BUSINESS) {
      getOrgResponses()
        .then((responses) => {
          if (Array.isArray(responses) && responses.length > 0) {
            setHasScopes(true);
          } else {
            setHasScopes(false);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch org responses in settings side nav", err);
          setHasScopes(false);
        });
    }
  }, [role]);

  useEffect(() => {
    // Only redirect when landing on base settings path, not when a sub-route is specified
    const atBase = /\/settings\/?$/i.test(location.pathname);
    if (atBase) {
      const isOwner = role === UserRole.ADMIN || role === UserRole.BUSINESS;
      navigate(isOwner ? "team" : "security", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, role]);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your account settings and security preferences</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px,1fr] gap-6 items-start">
        {/* Left nav */}
        <div className="rounded-lg bg-white p-4 shadow-sm border border-gray-200 sticky top-6 h-max" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          <div className="text-sm font-semibold text-gray-700 mb-4">Settings</div>
          <div className="space-y-2">
            {(role === UserRole.ADMIN || role === UserRole.BUSINESS) && (
              <LeftLink
                to="team"
                label="Team Management"
                icon={
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="7" width="18" height="13" rx="2" ry="2" />
                    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                  </svg>
                }
              />
            )}
            {(role === UserRole.ADMIN || role === UserRole.BUSINESS) && (
              <LeftLink
                to="activity"
                label="Activity Log"
                icon={
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                }
              />
            )}
            {role !== undefined && (role === UserRole.BUSINESS || role === UserRole.BUSINESS_ADMINISTRATOR) && (
              <LeftLink
                to="integrations"
                label="Integrations"
                icon={
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                }
              />
            )}
            {role !== undefined && (role === UserRole.BUSINESS || role === UserRole.BUSINESS_ADMINISTRATOR) && (
              <LeftLink
                to="sso"
                label="Single Sign-On (SSO)"
                icon={
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="10" rx="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                }
              />
            )}
            {role !== undefined && (role === UserRole.BUSINESS || hasScopes) && (
              <LeftLink
                to="scopes-history"
                label="History of Scopes"
                icon={
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                }
              />
            )}
            <LeftLink
              to="notifications"
              label="Notifications"
              icon={
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              }
            />
            <LeftLink
              to="security"
              label="Security"
              icon={
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="10" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              }
            />
          </div>
          {location.pathname.includes("notifications") && role !== UserRole.SOLUTIONS_SPECIALIST && (
            <NotificationInfoCard />
          )}
        </div>
 
        {/* Right content */}
        <div className="min-w-0">
          <Routes>
            {/* Team only for owners */}
            {(role === UserRole.ADMIN || role === UserRole.BUSINESS) && (
              <Route path="team/*" element={<TeamManagementPanel />} />
            )}
 
            {/* Wrap others in a card-like section */}
            {(role === UserRole.ADMIN || role === UserRole.BUSINESS) && (
              <Route
                path="activity/*"
                element={
                  <section className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
                    <ActivityLogPage />
                  </section>
                }
              />
            )}
            {/* Asset Inventory route moved out of OrgSettings layout */}
            {(role === UserRole.BUSINESS || role === UserRole.BUSINESS_ADMINISTRATOR) && (
              <Route
                path="integrations/*"
                element={
                  <section className="rounded-xl bg-white border border-gray-200 p-6 shadow-sm space-y-8">
                    {/* Header */}
                    <div>
                      <h2 className="text-xl text-gray-900 mb-1" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}>Integrations</h2>
                      <p className="text-sm text-gray-500">
                        Connect your security workflow with external platforms. Company-level settings apply to all programs.
                      </p>
                    </div>

                    {/* Company-level Slack + Jira */}
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-4">
                        Notifications &amp; Issue Tracking
                      </h3>
                      <CompanyIntegrations />
                    </div>

                    {/* Divider */}
                    <hr className="border-gray-200" />

                    {/* Vanta / other integrations */}
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-4">
                        Compliance
                      </h3>
                      <Integration />
                    </div>
                  </section>
                }
              />
            )}
            {(role === UserRole.BUSINESS || role === UserRole.BUSINESS_ADMINISTRATOR) && (
              <Route
                path="sso/*"
                element={<SSOSettings />}
              />
            )}
            {role !== undefined && (role === UserRole.BUSINESS || hasScopes) && (
              <Route
                path="scopes-history/*"
                element={
                  <section className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
                    <HistoryOfScopes />
                  </section>
                }
              />
            )}
            <Route
              path="notifications/*"
              element={
                <div className="space-y-6">
                  <NotificationSettings />
                  <div className="bg-white border border-gray-200 rounded-xl p-4 text-xs text-gray-500 shadow-sm">
                    When disabled, you will only receive critical emails like security alerts, password resets, and invitations.
                  </div>
                </div>
              }
            />
            <Route
              path="security/*"
              element={
                <section className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
                  <Security />
                </section>
              }
            />
            <Route
              path="*"
              element={
                <section className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
                  <Security />
                </section>
              }
            />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default OrgSettingsPage;


